import * as THREE from 'three';
import { OrbitControls } from 'three-stdlib';
import default360 from '../assets/img/default360.jpg';

/**
 * Three.js Utilities for 360° Scene Rendering
 * 
 * PROGRESSIVE IMAGE LOADING FEATURE:
 * This module implements progressive image loading for scene inventory (not hotspots).
 * 
 * How it works:
 * 1. When loading an image scene, the system first attempts to load a low-resolution version
 * 2. Low-res images follow the naming convention: original "image001.png" → low-res "image001.low.png"
 * 3. The low-res version is displayed immediately for faster perceived loading
 * 4. The high-resolution version loads in the background
 * 5. Once high-res is ready, it silently replaces the low-res version
 * 6. If low-res fails, it falls back to the original image
 * 7. If both fail, the current texture is maintained
 * 
 * Supported image formats: jpg, jpeg, png, gif, bmp, webp, tiff, tga
 * Video scenes and hotspot textures are not affected by this feature.
 * 
 * Example:
 * - Original: https://example.com/scenes/image001.png
 * - Low-res:  https://example.com/scenes/image001.low.png
 */

// Constants
const SPHERE_RADIUS = 500;
const SPHERE_SEGMENTS = { width: 64, height: 64 };
const CAMERA_FOV = 75;
const CAMERA_NEAR = 0.01;
const CAMERA_FAR = 2000;

// Add new constants for texture optimization
const MAX_TEXTURE_SIZE = 4096; // Maximum texture size in pixels
const TEXTURE_COMPRESSION = true; // Enable texture compression
const PROGRESSIVE_LOADING = true; // Enable progressive loading

/**
 * Create and configure a WebGL renderer
 * @param {HTMLElement} mountElement - DOM element to mount the renderer
 * @returns {THREE.WebGLRenderer} Configured renderer
 */
const createRenderer = (mountElement) => {
  const renderer = new THREE.WebGLRenderer({ 
    antialias: true,
    powerPreference: 'high-performance'
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  mountElement.appendChild(renderer.domElement);
  return renderer;
};

/**
 * Create and configure a perspective camera
 * @returns {THREE.PerspectiveCamera} Configured camera
 */
const createCamera = () => {
  return new THREE.PerspectiveCamera(
    CAMERA_FOV,
    window.innerWidth / window.innerHeight,
    CAMERA_NEAR,
    CAMERA_FAR
  );
};

/**
 * Create and configure a sphere with equirectangular texture
 * @param {THREE.Texture} texture - Initial texture to apply
 * @returns {THREE.Mesh} Configured sphere mesh
 */
const createSphere = (texture) => {
  const geometry = new THREE.SphereGeometry(
    SPHERE_RADIUS,
    SPHERE_SEGMENTS.width,
    SPHERE_SEGMENTS.height
  );
  geometry.scale(-1, 1, 1);

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    side: THREE.DoubleSide,
  });

  return new THREE.Mesh(geometry, material);
};

/**
 * Configure texture with equirectangular mapping and optimized settings
 * @param {THREE.Texture} texture - Texture to configure
 * @param {THREE.WebGLRenderer} renderer - Renderer for anisotropy
 */
const configureTexture = (texture, renderer) => {
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  
  // Optimize texture settings for performance
  texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 4); // Limit anisotropy
  texture.minFilter = THREE.LinearMipMapLinearFilter; // Better quality mipmaps
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  
  // Enable texture compression if supported
  if (TEXTURE_COMPRESSION && renderer.capabilities.isWebGL2) {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.format = THREE.RGBAFormat;
    texture.type = THREE.UnsignedByteType;
  }

  // Force texture size limits
  if (texture.image) {
    const maxSize = Math.min(MAX_TEXTURE_SIZE, renderer.capabilities.maxTextureSize);
    if (texture.image.width > maxSize || texture.image.height > maxSize) {
      const scale = maxSize / Math.max(texture.image.width, texture.image.height);
      texture.image.width *= scale;
      texture.image.height *= scale;
    }
  }
};

/**
 * Initialize Three.js scene with optimized renderer settings
 * @param {HTMLElement} mountRef - DOM element to mount the renderer
 * @param {Object} refs - Object containing necessary refs
 * @param {Object} initialCameraView - Initial camera position {x, y, z}
 */
export const initializeThree = (mountRef, refs, initialCameraView = { x: 0, y: 0, z: 0.05 }) => {
  const { rendererRef, sceneRef, cameraRef, controlsRef, sphereRef, textureRef } = refs;

  // Initialize scene objects with optimized settings
  const renderer = new THREE.WebGLRenderer({ 
    antialias: true,
    powerPreference: 'high-performance',
    precision: 'mediump', // Use medium precision for better performance
    stencil: false, // Disable stencil buffer if not needed
    depth: true,
    alpha: false // Disable alpha for better performance
  });
  
  // Optimize renderer settings
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.shadowMap.enabled = false; // Disable shadows if not needed
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  
  mountRef.current.appendChild(renderer.domElement);
  rendererRef.current = renderer;

  // Initialize scene objects
  const scene = new THREE.Scene();
  const camera = createCamera();
  
  // Set initial camera position immediately
  const { x, y, z } = initialCameraView;
  const zPos = Math.abs(z) < 1e-3 ? 2.0 : z;  // Start further away to allow zoom in
  camera.position.set(x, y, zPos);
  
  const initialTexture = new THREE.TextureLoader().load(default360);
  
  // Configure and store references
  configureTexture(initialTexture, renderer);
  sceneRef.current = scene;
  cameraRef.current = camera;
  textureRef.current = initialTexture;

  // Create and add sphere
  const sphere = createSphere(initialTexture);
  scene.add(sphere);
  sphereRef.current = sphere;

  // Setup controls with initial camera position
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = false; // Will be updated by editorMode effect
  controls.rotateSpeed = -0.5;    // Will be updated by editorMode effect
  controls.enableZoom = false;   // Disable OrbitControls zoom - using CSS zoom instead
  controls.target.set(0, 0, 0);
  controls.position0.set(x, y, zPos);
  controls.zoom0 = 1;
  
  // Add zoom event listeners for debugging
  controls.addEventListener('change', () => {
    const pos = camera.position.clone();
    const distance = pos.length();
    const isAtMinDistance = distance <= controls.minDistance;
    const isAtMaxDistance = distance >= controls.maxDistance;
  });
  
  controls.addEventListener('start', () => { });
  
  controls.addEventListener('end', () => { });
    
  controlsRef.current = controls;

  // CSS Zoom implementation
  let currentZoom = 1.0;
  const minZoom = 1.0;    // Don't allow zooming out beyond default size
  const maxZoom = 5.0;    // Allow zooming in up to 5x
  const zoomSpeed = 0.1;
  
  const updateCanvasZoom = (zoomLevel) => {
    currentZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel));
    renderer.domElement.style.transform = `scale(${currentZoom})`;
    renderer.domElement.style.transformOrigin = 'center center';
  };
  
  const resetZoom = () => {
    currentZoom = 1.0;
    renderer.domElement.style.transform = `scale(${currentZoom})`;
    renderer.domElement.style.transformOrigin = 'center center';
  };
  
  // Expose reset function globally for scene changes
  window._resetCanvasZoom = resetZoom;
  
  const handleWheel = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const delta = event.deltaY > 0 ? -zoomSpeed : zoomSpeed;
    const newZoom = currentZoom + delta;
    
    updateCanvasZoom(newZoom);
  };
  
  const handleMouseDown = (event) => { };
  
  renderer.domElement.addEventListener('wheel', handleWheel, { passive: false });
  renderer.domElement.addEventListener('mousedown', handleMouseDown);

  // Handle window resize
  const handleResize = () => {
    if (!cameraRef.current || !rendererRef.current) return;
    const { current: cam } = cameraRef;
    const { current: rend } = rendererRef;
    
    cam.aspect = window.innerWidth / window.innerHeight;
    cam.updateProjectionMatrix();
    rend.setSize(window.innerWidth, window.innerHeight);
    
    // Ensure proper sprite rendering after resize
    rend.sortObjects = true;
  };
  window.addEventListener('resize', handleResize);

  // Animation loop
  const animate = () => {
    requestAnimationFrame(animate);
    // Always update controls to handle both modes
    controls.update();
    renderer.render(scene, camera);
  };
  animate();

  // Return cleanup function
  return () => {
    window.removeEventListener('resize', handleResize);
    renderer.domElement.removeEventListener('wheel', handleWheel);
    renderer.domElement.removeEventListener('mousedown', handleMouseDown);
    window._resetCanvasZoom = null; // Clean up global reference
    controls.dispose();
    initialTexture.dispose();
    scene.traverse((obj) => {
      if (obj.isMesh?.material) obj.material.dispose();
    });
    if (mountRef.current?.contains(renderer.domElement)) {
      mountRef.current.removeChild(renderer.domElement);
    }
    renderer.dispose();
  };
};

/**
 * Generate low-resolution image URL from original URL
 * @param {string} originalUrl - Original image URL
 * @returns {string} Low-resolution image URL
 */
export const generateLowResUrl = (originalUrl) => {
  if (!originalUrl) return originalUrl;
  
  try {
    // Handle different URL formats
    const url = new URL(originalUrl, window.location.origin);
    const pathname = url.pathname;
    
    // Extract file extension and name
    const lastDotIndex = pathname.lastIndexOf('.');
    if (lastDotIndex === -1) return originalUrl;
    
    const extension = pathname.substring(lastDotIndex);
    const basePath = pathname.substring(0, lastDotIndex);
    
    // Check if it's already a low-res version to prevent infinite loops
    if (basePath.endsWith('.low')) {
      return originalUrl;
    }
    
    // Insert .low before the extension
    const lowResPath = basePath + '.low1' + extension;
    
    // Reconstruct URL with low-res path
    url.pathname = lowResPath;
    return url.toString();
  } catch (error) {
    console.warn('Error generating low-res URL, using original:', error);
    return originalUrl;
  }
};

/**
 * Load texture onto sphere with progressive loading (low-res first, then high-res)
 * @param {string} url - URL of the texture to load
 * @param {Object} refs - Object containing necessary refs
 * @param {Function} onLoaded - Callback function to call when the texture is loaded
 * @param {Object} cameraView - Camera position to set immediately {x, y, z}
 */


const generateVariantUrl = (originalUrl, variant) => {
  if (!originalUrl) return originalUrl;

  try {
    const url = new URL(originalUrl, window.location.origin);
    const pathname = url.pathname;

    const lastDotIndex = pathname.lastIndexOf(".");
    if (lastDotIndex === -1) return originalUrl;

    const extension = pathname.substring(lastDotIndex);
    const basePath = pathname.substring(0, lastDotIndex);

    if (basePath.endsWith(`.${variant}`)) {
      return originalUrl;
    }

    url.pathname = `${basePath}.${variant}${extension}`;
    return url.toString();
  } catch (error) {
    console.warn(`Error generating ${variant} URL:`, error);
    return originalUrl;
  }
};


// export const loadTextureOntoSphere = (url, refs, onLoaded, cameraView) => {
//   const { rendererRef, sphereRef, textureRef, cameraRef, controlsRef } = refs;
//   const material = sphereRef.current.material;
//   const currentTexture = material.map;
//   const loader = new THREE.TextureLoader();

//   console.log('loadTextureOntoSphere called with URL:', url);

//   // Set camera position immediately if provided
//   if (cameraView && cameraRef.current && controlsRef.current) {
//     const { x, y, z } = cameraView;
//     const zPos = Math.abs(z) < 1e-3 ? 0.05 : z;
//     cameraRef.current.position.set(x, y, zPos);
//     controlsRef.current.position0.set(x, y, zPos);
//     controlsRef.current.zoom0 = 1;
//   }

//   // Check if this is an image file (progressive loading only for images)
//   const isImageFile = /\.(jpg|jpeg|png|gif|bmp|webp|tiff|tga)$/i.test(url);
  
//   if (!isImageFile) {
//     console.log('Non-image file detected, using direct loading:', url);
//     // For non-image files, use direct loading
//     loader.load(
//       url,
//       (texture) => {
//         console.log('Direct texture loaded successfully:', url);
//         configureTexture(texture, rendererRef.current);
//         material.map = texture;
//         material.needsUpdate = true;
//         if (textureRef.current && textureRef.current !== currentTexture) {
//           textureRef.current.dispose();
//         }
//         textureRef.current = texture;
//         if (onLoaded) onLoaded();
//       },
//       undefined,
//       (err) => {
//         console.error('Failed to load texture:', err);
//         if (onLoaded) onLoaded();
//       }
//     );
//     return;
//   }

//   // Generate low-resolution URL for image files
//   const lowResUrl = generateLowResUrl(url);
//   console.log('Progressive loading for image file:', {
//     original: url,
//     lowRes: lowResUrl
//   });
  
//   // First, load the low-resolution version
//   loader.load(
//     lowResUrl,
//     (lowResTexture) => {
//       console.log('✅ Low-resolution texture loaded successfully:', lowResUrl);
//       configureTexture(lowResTexture, rendererRef.current);
//       material.map = lowResTexture;
//       material.needsUpdate = true;
//       if (textureRef.current && textureRef.current !== currentTexture) {
//         textureRef.current.dispose();
//       }
//       textureRef.current = lowResTexture;
      
//       // Call onLoaded immediately with low-res texture
//       if (onLoaded) onLoaded();
      
//       // Only start loading high-resolution version AFTER low-res is complete
//       console.log('🔄 Low-res complete, now starting high-resolution texture download:', url);
//       loader.load(
//         url,
//         (highResTexture) => {
//           console.log('✅ High-resolution texture loaded successfully:', url);
//           configureTexture(highResTexture, rendererRef.current);
//           material.map = highResTexture;
//           material.needsUpdate = true;
          
//           // Dispose of low-res texture and update reference
//           if (textureRef.current && textureRef.current !== highResTexture) {
//             textureRef.current.dispose();
//           }
//           textureRef.current = highResTexture;
          
//           // Note: We don't call onLoaded again since it was already called with low-res
//         },
//         undefined,
//         (err) => {
//           console.warn('⚠️ Failed to load high-resolution texture, keeping low-res version:', err);
//           // Keep the low-res texture if high-res fails
//         }
//       );
//     },
//     undefined,
//     (err) => {
//       console.warn('⚠️ Failed to load low-resolution texture, trying original URL:', err);
      
//       // If low-res fails, fall back to original URL
//       loader.load(
//         url,
//         (texture) => {
//           console.log('✅ Original texture loaded as fallback:', url);
//           configureTexture(texture, rendererRef.current);
//           material.map = texture;
//           material.needsUpdate = true;
//           if (textureRef.current && textureRef.current !== currentTexture) {
//             textureRef.current.dispose();
//           }
//           textureRef.current = texture;
//           if (onLoaded) onLoaded();
//         },
//         undefined,
//         (fallbackErr) => {
//           console.error('❌ Failed to load texture (both low-res and original):', fallbackErr);
//           // If both fail, keep the current texture
//           if (onLoaded) onLoaded();
//         }
//       );
//     }
//   );
// };


export const loadTextureOntoSphere = (url, refs, onLoaded, cameraView) => {
  const { rendererRef, sphereRef, textureRef, cameraRef, controlsRef } = refs;
  const material = sphereRef.current.material;
  const currentTexture = material.map;
  const loader = new THREE.TextureLoader();


  // Set camera position if provided
  if (cameraView && cameraRef.current && controlsRef.current) {
    const { x, y, z } = cameraView;
    const zPos = Math.abs(z) < 1e-3 ? 0.05 : z;
    cameraRef.current.position.set(x, y, zPos);
    controlsRef.current.position0.set(x, y, zPos);
    controlsRef.current.zoom0 = 1;
  }

  // Only progressive load for images
  const isImageFile = /\.(jpg|jpeg|png|gif|bmp|webp|tiff|tga)$/i.test(url);
  if (!isImageFile) {
    loader.load(
      url,
      (texture) => {
        configureTexture(texture, rendererRef.current);
        material.map = texture;
        material.needsUpdate = true;
        if (textureRef.current && textureRef.current !== currentTexture) {
          textureRef.current.dispose();
        }
        textureRef.current = texture;
        if (onLoaded) onLoaded();
      },
      undefined,
      (err) => {
        console.error("Failed to load texture:", err);
        if (onLoaded) onLoaded();
      }
    );
    return;
  }

  // Progressive: low1 → original (with race condition handling)
  const low1Url = generateVariantUrl(url, "low1");
  let loadingComplete = false;
  let low1Texture = null;
  let originalTexture = null;


  const applyTexture = (texture, isHighRes = false) => {
    if (loadingComplete) return;
    
    configureTexture(texture, rendererRef.current);
    material.map = texture;
    material.needsUpdate = true;
    if (textureRef.current && textureRef.current !== currentTexture) {
      textureRef.current.dispose();
    }
    textureRef.current = texture;

    if (isHighRes) {
      // Clear blur indicator and dispatch high-res ready event
      try {
        window._viewerIsLow1 = false;
        window.dispatchEvent(new CustomEvent('viewer-low1', { detail: { active: false } }));
        window.dispatchEvent(new CustomEvent('viewer-highres-ready', { detail: { ready: true, url } }));
      } catch (e) {}
      loadingComplete = true;
    } else {
      // Notify app that low1 is active
      try {
        window._viewerIsLow1 = true;
        window.dispatchEvent(new CustomEvent('viewer-low1', { detail: { active: true } }));
      } catch (e) {}
    }

    if (onLoaded) onLoaded();
  };

  // Start both loads simultaneously
  loader.load(
    low1Url,
    (texture) => {
      low1Texture = texture;
      if (!loadingComplete) {
        applyTexture(texture, false);
      }
    },
    undefined,
    (err) => {
      console.warn("⚠️ Failed to load low1 texture:", err);
    }
  );

  loader.load(
    url,
    (texture) => {
      originalTexture = texture;
      if (!loadingComplete) {
        applyTexture(texture, true);
      }
    },
    undefined,
    (err) => {
      console.warn("⚠️ Failed to load original texture:", err);
      // If original fails but low1 succeeded, keep low1
      if (low1Texture && !loadingComplete) {
        applyTexture(low1Texture, false);
      } else if (!loadingComplete) {
        console.error("❌ Failed to load both low1 and original textures");
        if (onLoaded) onLoaded();
      }
    }
  );
};



/**
 * Update camera position and reset controls based on camera view
 * @param {Object} cameraView - Object containing camera position coordinates
 * @param {Object} refs - Object containing necessary refs
 */
export const updateCameraAndControls = (cameraView, refs) => {
  const { cameraRef, controlsRef } = refs;
  const { x, y, z } = cameraView;
  const cam = cameraRef.current;
  const ctrls = controlsRef.current;
  
  if (!cam || !ctrls) return;

  // Prevent z=0 edge case
  const zPos = Math.abs(z) < 1e-3 ? 0.05 : z;
  
  // Update camera position smoothly without resetting controls
  cam.position.set(x, y, zPos);
  ctrls.target.set(0, 0, 0);
  
  // Update controls state without resetting
  ctrls.position0.set(x, y, zPos);
  ctrls.zoom0 = 1;
  
  // Don't call reset() as it can cause jumping
  // ctrls.reset();
  
  // Update projection matrix to ensure hotspots are visible
  cam.updateProjectionMatrix();
  
  // Ensure renderer is configured for sprite rendering
  if (refs.rendererRef.current) {
    refs.rendererRef.current.sortObjects = true;
  }
};

/**
 * Load a video texture onto the sphere
 * @param {string} url - URL of the video to load
 * @param {Object} refs - Object containing necessary refs
 * @param {boolean} muted - Whether the video should be muted
 * @param {Function} onLoaded - Callback function to call when the video is loaded
 * @param {boolean} loop - Whether the video should loop
 * @param {Object} cameraView - Camera position to set immediately {x, y, z}
 */
export const loadVideoTextureOntoSphere = (url, refs, muted = false, onLoaded, loop = true, cameraView) => {
  const { rendererRef, sphereRef, textureRef, cameraRef, controlsRef } = refs;
  if (textureRef.current && textureRef.current instanceof THREE.VideoTexture) {
    textureRef.current.dispose();
  }
  
  // Set camera position immediately if provided
  if (cameraView && cameraRef.current && controlsRef.current) {
    const { x, y, z } = cameraView;
    const zPos = Math.abs(z) < 1e-3 ? 0.05 : z;
    cameraRef.current.position.set(x, y, zPos);
    controlsRef.current.position0.set(x, y, zPos);
    controlsRef.current.zoom0 = 1;
  }
  
  const video = document.createElement('video');
  video.src = url;
  video.crossOrigin = 'anonymous';
  video.loop = loop;
  video.muted = muted;
  video.autoplay = true;
  video.playsInline = true;
  video.setAttribute('webkit-playsinline', '');
  video.onloadeddata = () => { if (onLoaded) onLoaded(); };
  video.play();
  window._threeDViewerVideo = video;
  const videoTexture = new THREE.VideoTexture(video);
  configureTexture(videoTexture, rendererRef.current);
  const material = sphereRef.current.material;
  material.map = videoTexture;
  material.needsUpdate = true;
  textureRef.current = videoTexture;
};

// Attach to window for quick access from Renderer.js
window.loadVideoTextureOntoSphere = loadVideoTextureOntoSphere;

// Hotspot utilities
export const createHotspot = (scene, camera, renderer, position, scale, inventory, onClick, onHover, onTextureLoaded, hotspotUuid) => {
  try {
    // Validate inputs
    if (!scene || !camera || !renderer || !position || !scale || !inventory) {
      console.error('createHotspot: Missing required parameters', { scene, camera, renderer, position, scale, inventory });
      return null;
    }

    // Check for existing hotspots with the same hotspot UUID and remove them
    const existingHotspots = scene.children.filter(child => 
      child.userData.type === 'hotspot' && 
      child.userData.hotspotUuid === hotspotUuid
    );
    
    existingHotspots.forEach(existingHotspot => {
      disposeHotspot(existingHotspot);
    });

    // Ensure position is within reasonable bounds to prevent clipping
    const adjustedPosition = {
      x: Math.max(-1000, Math.min(1000, position.x)),
      y: Math.max(-1000, Math.min(1000, position.y)),
      z: Math.max(-1000, Math.min(1000, position.z))
    };

    // Ensure minimum distance from camera to prevent clipping
    const minDistance = 50;
    const distance = Math.sqrt(adjustedPosition.x ** 2 + adjustedPosition.y ** 2 + adjustedPosition.z ** 2);
    if (distance < minDistance) {
      const scale = minDistance / distance;
      adjustedPosition.x *= scale;
      adjustedPosition.y *= scale;
      adjustedPosition.z *= scale;
    }

    // Create sprite material with proper settings
    const textureLoader = new THREE.TextureLoader();
    
    // Create material with initial opacity 0 and smooth transitions
    const material = new THREE.SpriteMaterial({
      side: THREE.DoubleSide,
      toneMapped: false,
      depthTest: false, // Disable depth testing to prevent clipping
      depthWrite: false, // Don't write to depth buffer for sprites
      transparent: true,
      opacity: 0, // Start with opacity 0
      blending: THREE.NormalBlending, // Use normal blending
      alphaTest: 0.1 // Discard pixels with alpha < 0.1
    });

    // Create sprite
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(scale.x, scale.y, scale.x); // Maintain aspect ratio
    sprite.position.set(adjustedPosition.x, adjustedPosition.y, adjustedPosition.z);
    sprite.visible = false; // Start invisible until texture is loaded
    sprite.renderOrder = 1; // Ensure hotspots render on top
    sprite.frustumCulled = false; // Disable frustum culling to prevent clipping

    // Store original scale and add transition properties for hover effect
    sprite.userData = {
      type: 'hotspot',
      inventory,
      hotspotUuid, // Store the hotspot UUID instead of inventory UUID
      onClick,
      onHover,
      originalScale: new THREE.Vector3(scale.x, scale.y, scale.x),
      isHovered: false,
      transitionStartTime: 0,
      transitionDuration: 200, // 200ms transition
      isTransitioning: false,
      animationFrameId: null // Track animation frame for cleanup
    };

    // Add to scene
    scene.add(sprite);

    // Update camera projection matrix to ensure hotspots are visible
    if (camera) {
      camera.updateProjectionMatrix();
    }

    // Ensure renderer is configured for sprite rendering
    if (renderer) {
      renderer.sortObjects = true; // Enable object sorting for proper sprite rendering
    }

    // Load texture with error handling
    textureLoader.load(
      inventory.path,
      (texture) => {
        try {
          // Dispose of previous texture if it exists
          if (material.map) {
            material.map.dispose();
          }
          
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.mapping = THREE.EquirectangularReflectionMapping;
          texture.colorSpace = THREE.SRGBColorSpace;
          if (renderer) {
            texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
          }
          material.map = texture;
          material.opacity = 0.8; // Set opacity to 1 when texture is loaded
          material.needsUpdate = true;
          sprite.visible = true; // Make visible when texture is loaded
          
          if (onTextureLoaded) onTextureLoaded();
        } catch (error) {
          console.error('Error configuring hotspot texture:', error);
        }
      },
      undefined,
      (error) => {
        console.error('Error loading hotspot texture:', error, 'Path:', inventory.path);
      }
    );

    return sprite;
  } catch (error) {
    console.error('Error creating hotspot:', error);
    return null;
  }
};

export const updateHotspotPosition = (hotspot, position) => {
  if (hotspot && position) {
    hotspot.position.set(position.x, position.y, position.z);
  }
};

export const updateHotspotScale = (hotspot, scale) => {
  if (hotspot && scale) {
    const newScale = new THREE.Vector3(scale.x, scale.y, scale.x);
    hotspot.scale.copy(newScale);
    hotspot.userData.originalScale.copy(newScale);
  }
};

export const updateHotspotTexture = (hotspot, texturePath, renderer) => {
  if (!hotspot || !texturePath) return;

  const textureLoader = new THREE.TextureLoader();
  textureLoader.load(
    texturePath,
    (texture) => {
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.mapping = THREE.EquirectangularReflectionMapping;
      texture.colorSpace = THREE.SRGBColorSpace;
      if (renderer) {
        texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
      }
      hotspot.material.map = texture;
      hotspot.material.needsUpdate = true;
    },
    undefined,
    (error) => {
      console.error('Error loading hotspot texture:', error);
    }
  );
};

export const handleHotspotHover = (hotspot, isHovered) => {
  if (!hotspot || !hotspot.userData) {
    console.warn('handleHotspotHover: Invalid hotspot or missing userData');
    return;
  }

  // Prevent duplicate hover states
  if (hotspot.userData.isHovered === isHovered) {
    return;
  }

  // Cancel any ongoing animation
  if (hotspot.userData.animationFrameId) {
    cancelAnimationFrame(hotspot.userData.animationFrameId);
    hotspot.userData.animationFrameId = null;
  }

  try {
    hotspot.userData.isHovered = isHovered;
    hotspot.userData.isTransitioning = true;
    hotspot.userData.transitionStartTime = Date.now();

    const originalScale = hotspot.userData.originalScale;
    const targetScale = isHovered ? 1.5 : 1.0;

    // Use GSAP-like easing for smooth transition
    const animateScale = () => {
      try {
        const elapsed = Date.now() - hotspot.userData.transitionStartTime;
        const progress = Math.min(elapsed / hotspot.userData.transitionDuration, 1);
        
        // Ease-out cubic function for smooth animation
        const easeOut = 1 - Math.pow(1 - progress, 3);
        
        const currentScale = 1 + (targetScale - 1) * easeOut;
        
        hotspot.scale.set(
          originalScale.x * currentScale,
          originalScale.y * currentScale,
          originalScale.x * currentScale
        );

        // Continue animation if not complete
        if (progress < 1) {
          hotspot.userData.animationFrameId = requestAnimationFrame(animateScale);
        } else {
          hotspot.userData.isTransitioning = false;
          hotspot.userData.animationFrameId = null;
        }
      } catch (error) {
        console.error('Error in hotspot hover animation:', error);
        hotspot.userData.isTransitioning = false;
        hotspot.userData.animationFrameId = null;
      }
    };

    animateScale();
  } catch (error) {
    console.error('Error handling hotspot hover:', error);
    hotspot.userData.isTransitioning = false;
    hotspot.userData.animationFrameId = null;
  }
};

export const disposeHotspot = (hotspot) => {
  if (!hotspot) return;

  // Cancel any ongoing animations
  if (hotspot.userData && hotspot.userData.animationFrameId) {
    cancelAnimationFrame(hotspot.userData.animationFrameId);
    hotspot.userData.animationFrameId = null;
  }

  // Reset hover state
  if (hotspot.userData) {
    hotspot.userData.isHovered = false;
    hotspot.userData.isTransitioning = false;
  }

  // Dispose of material and texture
  if (hotspot.material) {
    if (hotspot.material.map) {
      hotspot.material.map.dispose();
    }
    hotspot.material.dispose();
  }

  // Remove from parent
  if (hotspot.parent) {
    hotspot.parent.remove(hotspot);
  }
};

export const disposeAllHotspots = (scene) => {
  if (!scene) {
    return;
  }
  
  const hotspots = scene.children.filter(child => child.userData.type === 'hotspot');
  
  hotspots.forEach((hotspot, index) => {
    disposeHotspot(hotspot);
  });
};

export const createHotspotRaycaster = (camera, scene) => {
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();

  return {
    updateMousePosition: (event, container) => {
      const rect = container.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    },
    checkIntersections: () => {
      raycaster.setFromCamera(mouse, camera);
      return raycaster.intersectObjects(scene.children, true);
    },
    checkHotspotIntersections: () => {
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);
      return intersects.filter(intersect => intersect.object.userData.type === 'hotspot');
    },
    getWorldCoordinatesFromScreen: (normalizedX, normalizedY) => {
      const mouse = new THREE.Vector2(normalizedX, normalizedY);
      raycaster.setFromCamera(mouse, camera);
      
      // Find intersection with the sphere (which should be the first non-hotspot object)
      const allIntersects = raycaster.intersectObjects(scene.children, true);
      const sphereIntersect = allIntersects.find(intersect => 
        intersect.object.userData.type !== 'hotspot' && 
        intersect.object.geometry && 
        intersect.object.geometry.type === 'SphereGeometry'
      );
      
      if (sphereIntersect) {
        const point = sphereIntersect.point;
        return {
          x: point.x,
          y: point.y,
          z: point.z
        };
      }
      
      return null;
    }
  };
};

/**
 * Configure OrbitControls mouse buttons for different modes
 * @param {OrbitControls} controls - The OrbitControls instance
 * @param {boolean} isSelectMode - Whether select mode is active
 */
export const configureControlsForSelectMode = (controls, isSelectMode) => {
  if (!controls) return;
  
  if (isSelectMode) {
    // In select mode: disable left-click, enable right-click drag
    controls.mouseButtons = {
      LEFT: null, // Disable left-click camera movement
      MIDDLE: THREE.MOUSE.DOLLY, // Keep middle-click zoom
      RIGHT: THREE.MOUSE.ROTATE // Enable right-click for camera rotation
    };
  } else {
    // Normal mode: restore default mouse controls
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE, // Left-click for camera rotation
      MIDDLE: THREE.MOUSE.DOLLY, // Middle-click for zoom
      RIGHT: THREE.MOUSE.PAN // Right-click for panning
    };
  }
  
  controls.update();
};

/**
 * Smoothly animate camera to center on a hotspot position
 * @param {Object} hotspotPosition - The hotspot position {x, y, z}
 * @param {Object} refs - Object containing camera and controls refs
 * @param {number} duration - Animation duration in milliseconds (default: 1000)
 * @param {Function} onComplete - Optional callback when animation completes
 */
export const animateToHotspot = (hotspotPosition, refs, duration = 1000, onComplete) => {
  const { cameraRef, controlsRef } = refs;
  
  if (!cameraRef.current || !controlsRef.current || !hotspotPosition) {
    console.warn('animateToHotspot: Missing required parameters');
    return;
  }

  const camera = cameraRef.current;
  const controls = controlsRef.current;
  
  // Calculate direction from center to hotspot (normalized)
  const hotspotVector = new THREE.Vector3(hotspotPosition.x, hotspotPosition.y, hotspotPosition.z);
  const direction = hotspotVector.clone().normalize();
  
  // Preserve current camera distance (zoom level) from the center
  const currentDistance = camera.position.length();
  
  // Calculate new camera position maintaining the same distance but pointing towards hotspot
  const targetCameraPosition = direction.clone().multiplyScalar(-currentDistance);
  
  // Store initial positions
  const startPosition = camera.position.clone();
  const startTarget = controls.target.clone();
  
  // Target positions - keep the same target (sphere center) to maintain zoom
  const endPosition = targetCameraPosition;
  const endTarget = new THREE.Vector3(0, 0, 0); // Keep looking at sphere center
  
  // Animation variables
  let startTime = null;
  let animationId = null;
  
  const animate = (currentTime) => {
    if (!startTime) startTime = currentTime;
    
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Use easing function for smooth animation (ease-in-out)
    const easeInOut = progress < 0.5 
      ? 2 * progress * progress 
      : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    
    // Interpolate camera position
    const currentPosition = startPosition.clone().lerp(endPosition, easeInOut);
    const currentTarget = startTarget.clone().lerp(endTarget, easeInOut);
    
    // Update camera and controls
    camera.position.copy(currentPosition);
    controls.target.copy(currentTarget);
    controls.update();
    
    // Continue animation or complete
    if (progress < 1) {
      animationId = requestAnimationFrame(animate);
    } else {
      // Animation complete
      if (onComplete) onComplete();
    }
  };
  
  // Start animation
  animationId = requestAnimationFrame(animate);
  
  // Return function to cancel animation
  return () => {
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
  };
}; 